import { useState } from 'react'

import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../../common/utils/format-utils.js'
import { ChevronDown, ChevronUp } from '../../../../../../components/icon/solar-icons.js'
import { Section, SectionHeader } from '../../../../../../components/section/section.js'
import type { LeaveRequest } from '../../employee-profile-types.js'
import type { CarryoverRow } from './time-away-derive.js'
import { summarizeYearRequests } from './time-away-derive.js'
import { TimeAwayRequestList } from './time-away-request-list.js'

interface TimeAwayYearCardProps {
    title: string
    requests: LeaveRequest[]
    // Synthetic "+X days carried over" rows — current-year card only. They are
    // dated 1 Jan, so they sit naturally below the newest-first request rows.
    carryoverRows?: CarryoverRow[]
    lang: Language
    // Edit/cancel wiring forwarded to the history list — concluded rows are
    // gated row-by-row in the list via the shared time-off-edit-rules (owners
    // go through the approval flow, managers/admins act in place).
    editProps?: {
        isSelf: boolean
        canManage: boolean
        today: string
        onStartEdit?: (request: LeaveRequest) => void
        onCancelRequest?: (requestId: number) => void
        onDecideCancellation?: (requestId: number, action: 'approve' | 'reject') => Promise<boolean>
    }
}

export const TimeAwayYearCard = ({
    title,
    requests,
    carryoverRows = [],
    lang,
    editProps,
}: TimeAwayYearCardProps) => {
    const tOff = t.portal.timeOff

    // Humaans-style default: per-type totals, full breakdown behind "Näita
    // detailselt" (TASK-232). A row with an open deletion request is the
    // summary's blind spot — the manager acts on it here — so such a card
    // opens detailed.
    const [showDetails, setShowDetails] = useState(() =>
        requests.some((r) => r.cancel_requested_at_utc !== null),
    )
    const summary = summarizeYearRequests(requests)

    return (
        <Section className="time-away__year-card">
            <SectionHeader
                title={title}
                actions={
                    requests.length > 0 ? (
                        <button
                            type="button"
                            className="time-away__expander"
                            onClick={() => setShowDetails(!showDetails)}
                        >
                            {showDetails
                                ? tOff.view.showSummary(lang)
                                : tOff.view.showDetailed(lang)}
                            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                    ) : undefined
                }
            />
            {showDetails ? (
                <TimeAwayRequestList
                    requests={requests}
                    variant="history"
                    lang={lang}
                    {...editProps}
                />
            ) : (
                <div className="time-away__req-list">
                    {summary.map((row) => (
                        <div key={row.leaveTypeName} className="time-away__req-row">
                            <span className="time-away__req-emoji">{row.emoji}</span>
                            <span className="time-away__req-type">{row.leaveTypeName}</span>
                            <span className="time-away__req-days">
                                {tOff.view.dayCount(row.days)(lang)}
                            </span>
                            {/* Empty range/note keep the summary on the same
                                column grid as the carry-over row below it. */}
                            <span className="time-away__req-range" />
                            <span className="time-away__req-note" />
                        </div>
                    ))}
                </div>
            )}
            {carryoverRows.map((row) => (
                <div key={row.policyId} className="time-away__req-row time-away__carryover-row">
                    <span className="time-away__req-emoji">{row.emoji}</span>
                    <span className="time-away__req-type">
                        {row.leaveTypeName} — {tOff.balance.carryover(lang)}
                    </span>
                    <span className="time-away__req-days">
                        +{tOff.view.dayCount(row.amount)(lang)}
                    </span>
                    <span className="time-away__req-range">{formatDisplayDate(row.date)}</span>
                    <span className="time-away__req-note" />
                </div>
            ))}
        </Section>
    )
}
